import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { Send, X, Loader2, AlertCircle, Paperclip, File, Trash2 } from 'lucide-react';
import { useSendPatientMessage } from '@/hooks/usePatientMessages';
import { 
  validateMessageAttachmentFile,
  useAttachMessageDocument,
} from '@/hooks/useMessageAttachments';
import { useUploadDocument } from '@/hooks/useDocuments';
import { Button } from '@/components/ui/Button';

export interface MessageModalProps {
  patientId: string;
  patientName: string;
  clinicId: string;
  onClose: () => void;
}

export function MessageModal({
  patientId,
  patientName,
  onClose,
}: MessageModalProps) {
  const { t } = useTranslation();
  const sendMessage = useSendPatientMessage();
  const uploadDocument = useUploadDocument();
  const attachDocument = useAttachMessageDocument(undefined);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [message, setMessage] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const validFiles: File[] = [];
    
    for (const file of files) {
      const validationError = validateMessageAttachmentFile(file);
      if (validationError) {
        if (validationError === 'size_limit_exceeded') {
          toast.error(t('documents.errors.size_limit', `Filen ${file.name} er for stor (max 50 MB)`));
        } else if (validationError === 'invalid_type') {
          toast.error(t('documents.errors.invalid_type', `Filtypen for ${file.name} er ikke tilladt`));
        }
      } else {
        validFiles.push(file);
      }
    }
    
    setSelectedFiles((prev) => [...prev, ...validFiles]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const trimmedMessage = message.trim();
    if (!trimmedMessage && selectedFiles.length === 0) {
      return;
    }

    setError(null);
    setIsUploading(true);

    try {
      // First, send the message
      const sentMessage = await sendMessage.mutateAsync({
        patient_id: patientId,
        content: trimmedMessage || '(Fil vedhæftet)',
      });

      // Then upload and attach files if any
      if (selectedFiles.length > 0) {
        for (const file of selectedFiles) {
          const doc = await uploadDocument.mutateAsync({
            clinicId: (sentMessage as any).clinic_id,
            patientId: patientId,
            file: file,
            tags: ['message'],
          });
          
          await attachDocument.mutateAsync({
            message_id: (sentMessage as any).id,
            document_id: doc.id,
          });
        }
      }

      toast.success(t('messages.toasts.sent', 'Besked sendt'));
      onClose();
    } catch (err) {
      console.error('Error sending message:', err);
      setError(t('messages.errors.send_failed', 'Kunne ikke sende besked'));
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] overflow-y-auto bg-black/50 p-4">
      <div className="mx-auto mt-10 max-w-2xl rounded-xl bg-white shadow-xl dark:bg-card-dark">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            {t('messages.modal.title', 'Send besked')}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700"
            aria-label={t('common.close', 'Luk')}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <div className="mb-4 rounded-lg bg-red-50 p-4 dark:bg-red-900/20">
              <div className="flex">
                <AlertCircle className="h-5 w-5 text-red-400" />
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                    {error}
                  </h3>
                </div>
              </div>
            </div>
          )}

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
              {t('messages.modal.recipient', 'Modtager')}
            </label>
            <div className="rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-700 dark:bg-gray-800 dark:text-gray-200">
              {patientName}
            </div>
          </div>

          <div className="mb-4">
            <label
              htmlFor="message"
              className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2"
            >
              {t('messages.modal.message', 'Besked')}
            </label>
            <textarea
              id="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={6}
              placeholder={t('messages.modal.placeholder', 'Skriv din besked her...')}
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm 
                focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 
                dark:border-gray-600 dark:bg-gray-700 dark:text-white
                placeholder:text-gray-400 dark:placeholder:text-gray-500"
              disabled={isUploading}
            />
          </div>

          <div className="mb-6">
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileSelect}
              multiple
              accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.doc,.docx,.xls,.xlsx,.txt"
              className="hidden"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              className="inline-flex items-center gap-2 rounded-md bg-gray-100 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 disabled:opacity-50 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
            >
              <Paperclip className="h-4 w-4" />
              {t('messages.modal.attach_file', 'Vedhæft fil')}
            </button>
            
            {selectedFiles.length > 0 && (
              <div className="mt-3 space-y-2">
                {selectedFiles.map((file, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between rounded-md border border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-600 dark:bg-gray-700"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <File className="h-4 w-4 flex-shrink-0 text-gray-500 dark:text-gray-400" />
                      <span className="truncate text-sm text-gray-700 dark:text-gray-200">
                        {file.name}
                      </span>
                      <span className="flex-shrink-0 text-xs text-gray-500 dark:text-gray-400">
                        ({Math.round(file.size / 1024)} KB)
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveFile(index)}
                      disabled={isUploading}
                      className="ml-2 flex-shrink-0 rounded p-1 text-gray-400 hover:bg-gray-200 hover:text-gray-600 disabled:opacity-50 dark:hover:bg-gray-600"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={isUploading}
            >
              {t('common.cancel', 'Annuller')}
            </Button>
            <Button
              type="submit"
              disabled={isUploading || (!message.trim() && selectedFiles.length === 0)}
            >
              {isUploading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t('messages.modal.sending', 'Sender...')}
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  {t('messages.modal.send', 'Send')}
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
